import { Space } from 'antd';
import { SettingsIcon } from 'common/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useState } from 'react';
import { useAuth } from 'src/auth';
import { languageStored } from 'src/i18n.config';
import {
  NotificationButton,
  NotificationsContainer,
  useNotificationUnreadCount,
} from 'src/pages/notifications';

import { ProfileDrawer } from '../profile-drawer/profile-drawer.component';
import { useProfileButton } from './profile-button.hook';
import * as Styled from './profile-button.style';

interface ProfileButtonProps {
  onCloseMobileMenu?: () => void;
}

const ProfileButton = ({ onCloseMobileMenu }: ProfileButtonProps = {}) => {
  const { user } = useAuth();
  const { headerNotification } = useFlags();
  const notificationUnreadCount = useNotificationUnreadCount();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const {
    handleOnProfileButtonClick,
    isOpenProfileDrawer,
    handleOnCloseProfileDrawer,
    handleGetProfileName,
    handleGetProfileInitials,
  } = useProfileButton();

  const handleToggleNotifications = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setIsNotificationsOpen((prev) => !prev);
    },
    [],
  );

  const handleCloseNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
    onCloseMobileMenu?.();
  }, [onCloseMobileMenu]);

  return (
    user &&
    languageStored && (
      <>
        <ProfileDrawer
          open={isOpenProfileDrawer}
          onClose={() => handleOnCloseProfileDrawer()}
        />
        <NotificationsContainer
          isOpen={isNotificationsOpen}
          onClose={handleCloseNotifications}
        />
        <Styled.ProfileButtonWrapper
          onClick={handleOnProfileButtonClick}
          data-testid="profile-button"
        >
          <Space align="center" size={8}>
            <Styled.Avatar
              size={32}
              src={user?.photo_uri ?? undefined}
              alt={`${user?.first_name} ${user?.last_name}`}
            >
              {handleGetProfileInitials(user)}
            </Styled.Avatar>
            <Styled.TitleName level={4}>
              {handleGetProfileName(user)}
            </Styled.TitleName>
          </Space>

          <Space align="center" size={12}>
            {headerNotification && (
              <Styled.NotificationSlot
                onClick={handleToggleNotifications}
                data-testid="profile-button-notification"
              >
                <NotificationButton hasUnread={notificationUnreadCount > 0} />
              </Styled.NotificationSlot>
            )}
            <SettingsIcon />
          </Space>
        </Styled.ProfileButtonWrapper>
      </>
    )
  );
};

export { ProfileButton };
